import { CustomerKYCUpdateDTO } from "../entities/CustomerKYCUpdate";
import { resolveOnboardingChannel } from "../entities/OnboardingChannel";

type ApiMappingApplier = (
  updates: Partial<CustomerKYCUpdateDTO>,
  apiBody: Record<string, unknown>,
) => void;

function setIfDefined(value: unknown, apply: () => void): void {
  if (value !== undefined) {
    apply();
  }
}

function toYesNoFlag(value: unknown): "O" | "N" | undefined {
  if (value === undefined || value === null) return undefined;
  if (value === true || value === "true" || value === "O") return "O";
  if (value === false || value === "false" || value === "N") return "N";
  return undefined;
}

const API_FIELD_MAPPINGS: readonly ApiMappingApplier[] = [
  // Identity — /customers/prospect/{bk} uses snake_case first_name / last_name.
  (updates, apiBody) =>
    setIfDefined(updates.firstName, () => {
      apiBody.first_name = updates.firstName;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.lastName, () => {
      apiBody.last_name = updates.lastName;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.civilityCode, () => {
      apiBody.civility_code = updates.civilityCode;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.gender, () => {
      apiBody.gender = updates.gender;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.birthDate, () => {
      apiBody.birthDate = updates.birthDate;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.birthPlace, () => {
      apiBody.birthPlace = updates.birthPlace;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.nationalityCode, () => {
      apiBody.nationality_code = updates.nationalityCode;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.phone1, () => {
      apiBody.phone1 = updates.phone1;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.phone1CountryCode, () => {
      apiBody.phone1CountryCode = updates.phone1CountryCode;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.phone2, () => {
      apiBody.phone2 = updates.phone2;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.phone3, () => {
      apiBody.phone3 = updates.phone3;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.phone4, () => {
      apiBody.phone4 = updates.phone4;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.phone2CountryCode, () => {
      apiBody.phone2CountryCode = updates.phone2CountryCode;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.phone3CountryCode, () => {
      apiBody.phone3CountryCode = updates.phone3CountryCode;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.phone4CountryCode, () => {
      apiBody.phone4CountryCode = updates.phone4CountryCode;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.email1, () => {
      apiBody.email1 = updates.email1;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.email2, () => {
      apiBody.email2 = updates.email2;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.fax, () => {
      apiBody.fax = updates.fax;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.pep, () => {
      apiBody.peps = updates.pep;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.resident, () => {
      apiBody.resident = updates.resident;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.familyStatusCode, () => {
      apiBody.family_status_code = updates.familyStatusCode;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.fatherLastName, () => {
      apiBody.fatherLastName = updates.fatherLastName;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.motherMaidenName, () => {
      apiBody.motherMaidenName = updates.motherMaidenName;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.fatherFirstNames, () => {
      apiBody.fatherFirstName = updates.fatherFirstNames;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.motherFirstNames, () => {
      apiBody.motherFirstName = updates.motherFirstNames;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.deathDate, () => {
      apiBody.death_date = updates.deathDate;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.profession, () => {
      apiBody.profession = updates.profession;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.professionCode, () => {
      // /customers/prospect/{bk} uses snake_case profession_code
      // (unlike /update-identity which uses camelCase professionCode).
      apiBody.profession_code = updates.professionCode;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.employer, () => {
      apiBody.employer = updates.employer;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.sectorActivity, () => {
      // Scoring (ACTIVITE) reads sector_activity_code; sector_activity is the label.
      apiBody.sector_activity_code = updates.sectorActivity;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.sectorActivityLabel, () => {
      apiBody.sector_activity = updates.sectorActivityLabel;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.monthlyIncome, () => {
      apiBody.monthly_income = updates.monthlyIncome;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.qualityCode, () => {
      apiBody.qualityCode = updates.qualityCode;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.professionCountryCode, () => {
      apiBody.paysProfession = updates.professionCountryCode;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.professionCategory, () => {
      apiBody.profession_category = updates.professionCategory;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.incomeSource, () => {
      apiBody.income_source = updates.incomeSource;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.professionalPhone, () => {
      apiBody.professionalPhone = updates.professionalPhone;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.professionalPhoneCountryCode, () => {
      apiBody.professionalPhoneCountryCode =
        updates.professionalPhoneCountryCode;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.spouseLastName, () => {
      apiBody.spouseLastName = updates.spouseLastName;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.spouseFirstName, () => {
      apiBody.spouseFirstName = updates.spouseFirstName;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.numberChildren, () => {
      apiBody.number_children = updates.numberChildren;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.relationOtherFinancialInstitutions, () => {
      apiBody.otherBankRelation = toYesNoFlag(
        updates.relationOtherFinancialInstitutions,
      );
    }),
  (updates, apiBody) =>
    setIfDefined(updates.bankEmployee, () => {
      apiBody.bankPersonnel = toYesNoFlag(updates.bankEmployee);
    }),
  (updates, apiBody) =>
    setIfDefined(updates.patrimoineRealEstateCount, () => {
      apiBody.patrimoine_real_estate_count = updates.patrimoineRealEstateCount;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.patrimoineRealEstateAssets, () => {
      apiBody.patrimoine_real_estate_assets =
        updates.patrimoineRealEstateAssets;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.patrimoineRealEstateInsured, () => {
      apiBody.patrimoine_real_estate_insured =
        updates.patrimoineRealEstateInsured;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.patrimoineRealEstateEstimatedValue, () => {
      apiBody.patrimoine_real_estate_estimated_value =
        updates.patrimoineRealEstateEstimatedValue;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.patrimoineOtherBankAccountsValue, () => {
      apiBody.patrimoine_other_bank_accounts_value =
        updates.patrimoineOtherBankAccountsValue;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.patrimoineOtherBankAccounts, () => {
      apiBody.patrimoine_other_bank_accounts =
        updates.patrimoineOtherBankAccounts;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.patrimoineValuableObjects, () => {
      apiBody.patrimoine_valuable_objects = updates.patrimoineValuableObjects;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.patrimoineValuablesInsured, () => {
      apiBody.patrimoine_valuables_insured = updates.patrimoineValuablesInsured;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.patrimoineValuablesValue, () => {
      apiBody.patrimoine_valuables_value = updates.patrimoineValuablesValue;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.street, () => {
      apiBody.address = updates.street;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.postalAddress, () => {
      apiBody.postal_address = updates.postalAddress;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.poBox, () => {
      apiBody.poBox = updates.poBox;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.residenceDate, () => {
      apiBody.residenceDate = updates.residenceDate;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.city, () => {
      apiBody.city = updates.city;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.postalCode, () => {
      apiBody.postal_code = updates.postalCode;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.countryId, () => {
      apiBody.country_residence_code = updates.countryId;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.situationPatrimoniale, () => {
      apiBody.situation_patrimoniale = updates.situationPatrimoniale;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.sourceDesFonds, () => {
      apiBody.source_des_fonds = updates.sourceDesFonds;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.objetRelationBancaire, () => {
      apiBody.objet_relation_bancaire = updates.objetRelationBancaire;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.residenceFiscalePays, () => {
      apiBody.residence_fiscale_pays = updates.residenceFiscalePays;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.ppe, () => {
      apiBody.ppe = updates.ppe;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.fonctionPpeExercee, () => {
      apiBody.fonction_ppe_exercee = updates.fonctionPpeExercee;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.beneficiaireEffectif, () => {
      apiBody.beneficiaire_effectif = updates.beneficiaireEffectif;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.identiteBeneficiaireEffectif, () => {
      apiBody.identite_beneficiaire_effectif =
        updates.identiteBeneficiaireEffectif;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.accountPurposeCode, () => {
      apiBody.account_purpose_code = updates.accountPurposeCode;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.onboardingChannel, () => {
      apiBody.onboarding_channel = resolveOnboardingChannel(
        updates.onboardingChannel,
      );
    }),
  (updates, apiBody) =>
    setIfDefined(updates.comment, () => {
      apiBody.comment = updates.comment;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.contactForSavingCardCredit, () => {
      apiBody.contactForSavingCardCredit = updates.contactForSavingCardCredit;
    }),
  (updates, apiBody) =>
    setIfDefined(updates.products, () => {
      apiBody.products = updates.products;
    }),
];

/**
 * Maps domain update DTO to API request body format.
 * Only includes fields that are present in the updates object.
 */
export class CustomerKYCUpdateMapper {
  /**
   * Transform partial update DTO to API body format.
   * Only changed fields are included in the result.
   */
  static toApiDTO(
    updates: Partial<CustomerKYCUpdateDTO>,
  ): Record<string, unknown> {
    const apiBody: Record<string, unknown> = {};
    for (const applyMapping of API_FIELD_MAPPINGS) {
      applyMapping(updates, apiBody);
    }

    return apiBody;
  }
}
